import { Blockchain } from "@anaconda/core/blockchain/types"
import {
  CollectionSearchSort,
  CollectionSearchSortDirection,
  CollectionStatisticPeriod,
} from "@anaconda/core/nft-collection/search/types"

export const AVAILABLE_STATISTIC_PERIODS = [
  CollectionStatisticPeriod.H1,
  CollectionStatisticPeriod.H6,
  CollectionStatisticPeriod.DAY,
  CollectionStatisticPeriod.WEEK,
  CollectionStatisticPeriod.MONTH,
] as const

export type AvailableStatisticPeriod =
  (typeof AVAILABLE_STATISTIC_PERIODS)[number]

/**
 * Periods with data on Solana. Its floors and floor changes come from other
 * marketplaces, and Tensor publishes a floor change for 24h and 7d on every
 * collection; its 1h change is missing for about half of ours, and it has no
 * 6h or 30d change at all.
 */
export const SOLANA_STATISTIC_PERIODS: readonly AvailableStatisticPeriod[] = [
  CollectionStatisticPeriod.DAY,
  CollectionStatisticPeriod.WEEK,
]

/**
 * Periods with data on Robinhood. Its sales, volume and top offers come from
 * OpenSea, which publishes sales and volume for 24 hours, 7 days and 30 days
 * only.
 */
export const ROBINHOOD_STATISTIC_PERIODS: readonly AvailableStatisticPeriod[] =
  [
    CollectionStatisticPeriod.DAY,
    CollectionStatisticPeriod.WEEK,
    CollectionStatisticPeriod.MONTH,
  ]

const STATISTIC_PERIODS_BY_BLOCKCHAIN = new Map<
  string,
  readonly AvailableStatisticPeriod[]
>([
  [Blockchain.SOLANA, SOLANA_STATISTIC_PERIODS],
  [Blockchain.ROBINHOOD, ROBINHOOD_STATISTIC_PERIODS],
])

/** `blockchain` as the page names it: a `Blockchain`, "ALL", or a URL slug. */
export function getAvailableStatisticPeriods(
  blockchain: string,
): readonly AvailableStatisticPeriod[] {
  return (
    STATISTIC_PERIODS_BY_BLOCKCHAIN.get(blockchain.toUpperCase()) ??
    AVAILABLE_STATISTIC_PERIODS
  )
}

/** `period` if the chain offers it, otherwise the chain's default period. */
export function resolveStatisticPeriod(
  blockchain: string,
  period: AvailableStatisticPeriod,
): AvailableStatisticPeriod {
  return getAvailableStatisticPeriods(blockchain).includes(period)
    ? period
    : getDefaultStatisticPeriod(blockchain)
}

export const DEFAULT_FILTER: AvailableStatisticPeriod =
  CollectionStatisticPeriod.DAY

/**
 * The period a leaderboard opens on without `?period=`, and so the one its URL
 * leaves out: 7 days on Solana, where 24 hours ranks too few trades to order
 * collections by, 24 hours elsewhere.
 */
export function getDefaultStatisticPeriod(
  blockchain: string,
): AvailableStatisticPeriod {
  return blockchain.toUpperCase() === Blockchain.SOLANA
    ? CollectionStatisticPeriod.WEEK
    : DEFAULT_FILTER
}

export const DEFAULT_SORT = {
  field: CollectionSearchSort.VOLUME,
  direction: CollectionSearchSortDirection.DESC,
}

export const INITIAL_FILTERS = {
  size: 30,
  statisticPeriod: DEFAULT_FILTER,
  blockchains: [] as Blockchain[],
}
